import {
    AlertTriangleIcon,
    BarChart3Icon,
    BookOpenIcon,
    CheckIcon,
    EyeIcon,
    Loader2Icon,
    PlayIcon,
    ShieldCheckIcon,
    SparklesIcon,
    TrendingUpIcon,
    XIcon,
} from "lucide-react";
import {useCallback, useState} from "react";
import Markdown from "react-markdown";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@components/ui/card";
import {Skeleton} from "@components/ui/skeleton";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@components/ui/table";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@components/ui/tooltip";
import {
    applySuggestion,
    approveSuggestion,
    generateNarrative,
    InsightsMonthlyTrend,
    InsightsNarrativeReport,
    InsightsTrendSeries,
    KnowledgeMetricKPI,
    KnowledgeSuggestion,
    previewApplySuggestion,
    rejectSuggestion,
    SuggestionPreviewData,
    useVNextBusinessContext,
    useVNextInsights,
} from "@hooks/use-vnext";

interface InsightsTabProps {
    workspaceId: string;
    scenarioId: string;
}

function formatNumber(value: number): string {
    if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
    if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
    return `$${value.toFixed(2)}`;
}

function ConfidenceBadge({confidence}: {confidence: number}) {
    const pct = Math.round(confidence * 100);
    const variant = pct >= 75 ? "default" : pct >= 50 ? "secondary" : "outline";
    return <Badge variant={variant}>{pct}%</Badge>;
}

function StatusBadge({status}: {status: string}) {
    const variants: Record<string, {variant: "default" | "secondary" | "outline" | "destructive"; label: string}> = {
        proposed: {variant: "secondary", label: "Proposed"},
        approved: {variant: "default", label: "Approved"},
        rejected: {variant: "outline", label: "Rejected"},
        applied: {variant: "default", label: "Applied"},
    };
    const config = variants[status] ?? {variant: "outline" as const, label: status};
    return <Badge variant={config.variant}>{config.label}</Badge>;
}

// ── Block 1: Overview KPIs ──────────────────────────────────

function OverviewKPIs({kpis}: {kpis: KnowledgeMetricKPI[]}) {
    if (kpis.length === 0) return null;

    const sorted = [...kpis].sort((a, b) => {
        const aIsTotal = a.metric_key.endsWith("_total") ? 0 : 1;
        const bIsTotal = b.metric_key.endsWith("_total") ? 0 : 1;
        return aIsTotal - bIsTotal || Math.abs(b.value) - Math.abs(a.value);
    });
    const display = sorted.slice(0, 6);

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                    <BarChart3Icon className="h-4 w-4" />
                    Overview KPIs
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {display.map((kpi, i) => (
                        <div key={i} className="rounded-lg border p-3">
                            <p className="text-xs text-muted-foreground truncate">{kpi.label}</p>
                            <p className="text-xl font-bold mt-0.5">
                                {kpi.meta?.unit === "percentage"
                                    ? `${kpi.value.toFixed(1)}%`
                                    : formatNumber(kpi.value)}
                            </p>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

// ── Block 2: Monthly Trend (compact) ────────────────────────

function formatTrendValue(value: number, unit: string): string {
    if (unit === "percentage") return `${value.toFixed(1)}%`;
    return formatNumber(value);
}

function friendlySeriesName(series: InsightsTrendSeries): string {
    return series.metric_key
        .replace(/_monthly$/, "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c: string) => c.toUpperCase());
}

function MonthlyTrendTable({trend}: {trend: InsightsMonthlyTrend}) {
    if (!trend || trend.months.length === 0) return null;

    const {series} = trend;
    // Show max 12 months, most recent first
    const months = trend.months.slice(-12);
    const seriesMap = new Map(series.map((s) => [s.series_key, s]));
    const hasTotal = months.some((m) => m.aggregates.total !== undefined);
    const hasAvgPct = months.some((m) => m.aggregates.avg_pct !== undefined);

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                    <TrendingUpIcon className="h-4 w-4" />
                    Monthly Trends
                </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Month</TableHead>
                            {series.map((s) => (
                                <TableHead key={s.series_key} className="text-right">
                                    {friendlySeriesName(s)}
                                </TableHead>
                            ))}
                            {hasTotal && <TableHead className="text-right font-semibold">Total</TableHead>}
                            {hasAvgPct && <TableHead className="text-right font-semibold">Avg %</TableHead>}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {months.map((m) => (
                            <TableRow key={m.month_key}>
                                <TableCell className="font-medium">{m.month_key}</TableCell>
                                {series.map((s) => {
                                    const val = m.points[s.series_key];
                                    const meta = seriesMap.get(s.series_key);
                                    return (
                                        <TableCell key={s.series_key} className="text-right">
                                            {val !== undefined
                                                ? formatTrendValue(val, meta?.unit ?? "currency")
                                                : "\u2014"}
                                        </TableCell>
                                    );
                                })}
                                {hasTotal && (
                                    <TableCell className="text-right font-semibold">
                                        {m.aggregates.total !== undefined ? formatNumber(m.aggregates.total) : "\u2014"}
                                    </TableCell>
                                )}
                                {hasAvgPct && (
                                    <TableCell className="text-right font-semibold">
                                        {m.aggregates.avg_pct !== undefined ? `${m.aggregates.avg_pct.toFixed(1)}%` : "\u2014"}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

// ── Block 3: Suggestions ────────────────────────────────────

function SuggestionPreviewPanel({preview}: {preview: SuggestionPreviewData}) {
    if (preview.deltas.length === 0) {
        return <p className="text-sm text-muted-foreground py-2">No model changes predicted.</p>;
    }
    return (
        <div className="mt-2 rounded-md border p-3 bg-muted/30">
            <p className="text-xs font-medium mb-2">Preview: {preview.deltas.length} cell(s) affected</p>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="text-xs">Node</TableHead>
                        <TableHead className="text-xs">Month</TableHead>
                        <TableHead className="text-xs text-right">Before</TableHead>
                        <TableHead className="text-xs text-right">After</TableHead>
                        <TableHead className="text-xs text-right">Delta</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {preview.deltas.slice(0, 10).map((d, i) => (
                        <TableRow key={i}>
                            <TableCell className="text-xs">{d.node_label || d.cell}</TableCell>
                            <TableCell className="text-xs">{d.month_key}</TableCell>
                            <TableCell className="text-xs text-right">{formatNumber(parseFloat(d.before))}</TableCell>
                            <TableCell className="text-xs text-right">{formatNumber(parseFloat(d.after))}</TableCell>
                            <TableCell className="text-xs text-right font-medium">
                                {formatNumber(parseFloat(d.delta))}
                                {d.delta_pct ? ` (${d.delta_pct}%)` : ""}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {preview.deltas.length > 10 && (
                <p className="text-xs text-muted-foreground mt-1">
                    + {preview.deltas.length - 10} more changes
                </p>
            )}
        </div>
    );
}

function AppliedAuditBadge({suggestion}: {suggestion: KnowledgeSuggestion}) {
    if (suggestion.status !== "applied") return null;
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger>
                    <Badge variant="default" className="text-xs">Applied</Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                    {suggestion.applied_at && (
                        <p className="text-xs">Applied: {new Date(suggestion.applied_at).toLocaleString()}</p>
                    )}
                    {suggestion.applied_by && (
                        <p className="text-xs">By: {suggestion.applied_by}</p>
                    )}
                    {suggestion.applied_ops?.map((op) => (
                        <p key={op.op_id} className="text-xs mt-1">
                            Op: {op.op_type} &rarr; {op.target_node_label || op.op_label}
                        </p>
                    ))}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

function SuggestionsBlock({
    suggestions,
    onAction,
    onPreview,
    actionLoading,
    previewData,
}: {
    suggestions: KnowledgeSuggestion[];
    onAction: (id: string, action: "approve" | "reject" | "apply") => void;
    onPreview: (id: string) => void;
    actionLoading: string | null;
    previewData: Record<string, SuggestionPreviewData>;
}) {
    const [showApplied, setShowApplied] = useState(false);

    if (suggestions.length === 0) return null;

    const filtered = showApplied
        ? suggestions
        : suggestions.filter((s) => s.status === "proposed" || s.status === "approved");

    const appliedCount = suggestions.filter((s) => s.status === "applied").length;

    const canApprove = (s: KnowledgeSuggestion) => s.status === "proposed";
    const canReject = (s: KnowledgeSuggestion) => s.status !== "applied" && s.status !== "rejected";
    const canApply = (s: KnowledgeSuggestion) =>
        s.status === "approved" || (s.status === "proposed" && s.confidence >= 0.75);
    const canPreview = (s: KnowledgeSuggestion) => s.status !== "applied" && s.status !== "rejected";

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                    <SparklesIcon className="h-4 w-4" />
                    Applied Data Mappings
                </CardTitle>
                <CardDescription>
                    Proposed mappings from extracted data to your financial model
                </CardDescription>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Type</TableHead>
                            <TableHead>Target Node</TableHead>
                            <TableHead>Rationale</TableHead>
                            <TableHead className="text-center">Confidence</TableHead>
                            <TableHead className="text-center">Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filtered.map((s) => {
                            const isLoading = actionLoading === s.id;
                            const preview = previewData[s.id];
                            return (
                                <>
                                    <TableRow key={s.id}>
                                        <TableCell>
                                            <Badge variant="outline">{s.type.replace("_", " ")}</Badge>
                                        </TableCell>
                                        <TableCell className="font-medium">{s.target_label || "\u2014"}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                                            {s.rationale || "\u2014"}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <ConfidenceBadge confidence={s.confidence} />
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <StatusBadge status={s.status} />
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {isLoading && (
                                                    <Loader2Icon className="h-4 w-4 animate-spin" />
                                                )}
                                                {canPreview(s) && (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    disabled={isLoading}
                                                                    onClick={() => onPreview(s.id)}
                                                                >
                                                                    <EyeIcon className="h-3 w-3" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Preview impact</TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                                {canApprove(s) && (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    disabled={isLoading}
                                                                    onClick={() => onAction(s.id, "approve")}
                                                                >
                                                                    <CheckIcon className="h-3 w-3" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Approve</TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                                {canReject(s) && (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    disabled={isLoading}
                                                                    onClick={() => onAction(s.id, "reject")}
                                                                >
                                                                    <XIcon className="h-3 w-3" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Reject</TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                                {canApply(s) && (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    size="sm"
                                                                    variant="default"
                                                                    disabled={isLoading}
                                                                    onClick={() => onAction(s.id, "apply")}
                                                                >
                                                                    <PlayIcon className="h-3 w-3 mr-1" />
                                                                    Apply
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Apply as VNextOp to model</TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                                <AppliedAuditBadge suggestion={s} />
                                                {s.status === "rejected" && (
                                                    <Badge variant="outline" className="text-xs">Rejected</Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                    {preview && (
                                        <TableRow key={`${s.id}-preview`}>
                                            <TableCell colSpan={6}>
                                                <SuggestionPreviewPanel preview={preview} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </>
                            );
                        })}
                    </TableBody>
                </Table>
                {!showApplied && appliedCount > 0 && (
                    <button
                        className="mt-3 text-xs text-muted-foreground hover:text-foreground underline"
                        onClick={() => setShowApplied(true)}
                    >
                        Show {appliedCount} applied suggestion{appliedCount !== 1 ? "s" : ""}
                    </button>
                )}
                {showApplied && appliedCount > 0 && (
                    <button
                        className="mt-3 text-xs text-muted-foreground hover:text-foreground underline"
                        onClick={() => setShowApplied(false)}
                    >
                        Hide applied
                    </button>
                )}
            </CardContent>
        </Card>
    );
}

// ── Block 4: Business Context Audit Panel ───────────────────

function BusinessContextAuditPanel({workspaceId}: {workspaceId: string}) {
    const {contextItems, isLoading} = useVNextBusinessContext(workspaceId, "approved");

    if (isLoading || contextItems.length === 0) return null;

    const contextTypeLabels: Record<string, string> = {
        event: "Event",
        decision: "Decision",
        risk: "Risk",
        one_off: "One-Off",
        constraint: "Constraint",
        operational_driver: "Op. Driver",
    };

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                    <ShieldCheckIcon className="h-4 w-4" />
                    Approved Business Context
                </CardTitle>
                <CardDescription>
                    Included in commentary generation
                </CardDescription>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="text-xs">Title</TableHead>
                            <TableHead className="text-xs">Type</TableHead>
                            <TableHead className="text-xs">Impact</TableHead>
                            <TableHead className="text-xs">Direction</TableHead>
                            <TableHead className="text-xs">Source</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {contextItems.map((ci) => (
                            <TableRow key={ci.id}>
                                <TableCell className="text-sm font-medium max-w-[250px] truncate">
                                    {ci.title}
                                </TableCell>
                                <TableCell>
                                    <Badge variant="outline" className="text-xs">
                                        {contextTypeLabels[ci.context_type] ?? ci.context_type}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                    {ci.impact_hint || "\u2014"}
                                </TableCell>
                                <TableCell className="text-xs">
                                    {ci.direction === "up" ? "\u2191" : ci.direction === "down" ? "\u2193" : "\u2194"}
                                    {" "}{ci.direction}
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">
                                    {ci.source_document_filename || "\u2014"}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

// ── Block 5: Narrative ──────────────────────────────────────

function NarrativeSection({
    hasNarrative,
    narrativeStale,
    narrative,
    onGenerate,
    isGenerating,
}: {
    hasNarrative: boolean;
    narrativeStale: boolean;
    narrative: InsightsNarrativeReport | null;
    onGenerate: (force?: boolean) => void;
    isGenerating: boolean;
}) {
    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <BookOpenIcon className="h-4 w-4" />
                        Commentary
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        {hasNarrative && narrativeStale && (
                            <Badge variant="outline" className="text-yellow-600">Stale</Badge>
                        )}
                        <Button
                            size="sm"
                            variant={hasNarrative && !narrativeStale ? "outline" : "default"}
                            disabled={isGenerating}
                            onClick={() => onGenerate(hasNarrative)}
                        >
                            {isGenerating ? (
                                <Loader2Icon className="h-3 w-3 animate-spin mr-1" />
                            ) : (
                                <SparklesIcon className="h-3 w-3 mr-1" />
                            )}
                            {hasNarrative ? "Regenerate" : "Generate Commentary"}
                        </Button>
                    </div>
                </div>
            </CardHeader>
            {narrative && (
                <CardContent>
                    <div
                        className="prose prose-sm dark:prose-invert max-w-none overflow-y-auto"
                        style={{resize: "vertical", minHeight: 80, maxHeight: 500}}
                    >
                        <Markdown>{narrative.content_markdown}</Markdown>
                    </div>
                    {narrative.generated_at && (
                        <p className="text-xs text-muted-foreground mt-3">
                            Generated {new Date(narrative.generated_at).toLocaleString()}
                        </p>
                    )}
                </CardContent>
            )}
            {!narrative && !isGenerating && (
                <CardContent>
                    <p className="text-sm text-muted-foreground">
                        No commentary generated yet. Click &quot;Generate Commentary&quot; to create an
                        LLM analysis of your extracted insights and business context.
                    </p>
                </CardContent>
            )}
        </Card>
    );
}

// ── Main InsightsTab ─────────────────────────────────────────

export function InsightsTab({workspaceId}: InsightsTabProps) {
    const {data, isLoading, error, refresh} = useVNextInsights(workspaceId);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [previewData, setPreviewData] = useState<Record<string, SuggestionPreviewData>>({});
    const [narrativeReport, setNarrativeReport] = useState<InsightsNarrativeReport | null>(null);
    const [isGeneratingNarrative, setIsGeneratingNarrative] = useState(false);

    const handleSuggestionAction = useCallback(
        async (suggestionId: string, action: "approve" | "reject" | "apply") => {
            setActionLoading(suggestionId);
            setActionError(null);
            try {
                if (action === "approve") await approveSuggestion(workspaceId, suggestionId);
                else if (action === "reject") await rejectSuggestion(workspaceId, suggestionId);
                else await applySuggestion(workspaceId, suggestionId);
                await refresh();
            } catch (err) {
                setActionError(err instanceof Error ? err.message : `Failed to ${action} suggestion`);
            } finally {
                setActionLoading(null);
            }
        },
        [workspaceId, refresh],
    );

    const handlePreview = useCallback(
        async (suggestionId: string) => {
            if (previewData[suggestionId]) {
                setPreviewData((prev) => {
                    const next = {...prev};
                    delete next[suggestionId];
                    return next;
                });
                return;
            }
            setActionLoading(suggestionId);
            setActionError(null);
            try {
                const result = await previewApplySuggestion(workspaceId, suggestionId);
                setPreviewData((prev) => ({...prev, [suggestionId]: result}));
            } catch (err) {
                setActionError(err instanceof Error ? err.message : "Failed to load preview");
            } finally {
                setActionLoading(null);
            }
        },
        [workspaceId, previewData],
    );

    const handleGenerateNarrative = useCallback(
        async (force?: boolean) => {
            setIsGeneratingNarrative(true);
            setActionError(null);
            try {
                const result = await generateNarrative(workspaceId, force);
                setNarrativeReport(result.report);
            } catch (err) {
                setActionError(err instanceof Error ? err.message : "Narrative generation failed");
            } finally {
                setIsGeneratingNarrative(false);
            }
        },
        [workspaceId],
    );

    if (isLoading && !data) {
        return (
            <div className="space-y-4 p-4">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-48 w-full" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center gap-2 p-4 text-destructive">
                <AlertTriangleIcon className="h-5 w-5" />
                {error}
            </div>
        );
    }

    const kpis = data?.kpis ?? [];
    const trend = data?.monthly_trend ?? {months: [], series: []};
    const suggestions = data?.suggestions ?? [];

    return (
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto space-y-4 p-4">
            {actionError && (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                    {actionError}
                </div>
            )}

            <OverviewKPIs kpis={kpis} />

            <MonthlyTrendTable trend={trend} />

            <SuggestionsBlock
                suggestions={suggestions}
                onAction={handleSuggestionAction}
                onPreview={handlePreview}
                actionLoading={actionLoading}
                previewData={previewData}
            />

            <BusinessContextAuditPanel workspaceId={workspaceId} />

            <NarrativeSection
                hasNarrative={data?.has_narrative ?? false}
                narrativeStale={data?.narrative_stale ?? false}
                narrative={narrativeReport}
                onGenerate={handleGenerateNarrative}
                isGenerating={isGeneratingNarrative}
            />
        </div>
    );
}
